import { useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import {
  Badge,
  Input,
  Checkbox,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Disclosure,
} from '@vnetwork/ui/primitives';
import { ruleCidr } from '@/api/opennebula';
import { SectionCard } from './shared';

interface SecgroupRuleLite {
  rule_type?: string;
  protocol?: string;
  range?: string;
  ip?: string;
  size?: string;
}

interface Secgroup {
  id: string;
  name?: string;
  template?: { rule?: SecgroupRuleLite[] };
}

/** Cap matches the OpenNebula instance attach limit enforced in the legacy console. */
const MAX_SECGROUPS = 10;

const ruleCount = (n: number) => `${n} rule${n === 1 ? '' : 's'}`;

/** Compact rule listing shown inside a selected group's accordion. */
function RuleLines({ rules }: { rules: SecgroupRuleLite[] }) {
  if (!rules.length) {
    return (
      <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
        No rules defined.
      </p>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
      {rules.map((r, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--vn-space-2)',
            fontSize: 'var(--vn-font-size-sm)',
          }}
        >
          <Badge size="sm" appearance="subtle" tone={r.rule_type === 'INBOUND' ? 'info' : 'neutral'}>
            {r.rule_type === 'INBOUND' ? 'In' : 'Out'}
          </Badge>
          <span style={{ fontWeight: 500 }}>{r.protocol || 'ALL'}</span>
          <span style={{ color: 'var(--vn-fg-muted)' }}>{r.range ? `port ${r.range}` : 'all ports'}</span>
          <span style={{ marginLeft: 'auto', color: 'var(--vn-fg-muted)' }}>{ruleCidr(r.ip, r.size) || 'any'}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Firewall Settings — attach security groups via a collapsed, searchable
 * multi-select so the form stays compact even with a large inventory of groups.
 * Selected groups surface as chips; their rules expand on demand.
 */
export function FirewallCard({
  secgroups,
  selected,
  onChange,
}: {
  secgroups: Secgroup[];
  selected: string[];
  onChange: (s: string[]) => void;
}) {
  const [query, setQuery] = useState('');

  const byId = useMemo(() => new Map(secgroups.map((sg) => [sg.id, sg])), [secgroups]);
  const selectedSgs = selected.map((id) => byId.get(id)).filter(Boolean) as Secgroup[];
  const atCap = selected.length >= MAX_SECGROUPS;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return secgroups;
    return secgroups.filter((sg) => (sg.name || sg.id).toLowerCase().includes(q));
  }, [secgroups, query]);

  const toggle = (id: string) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else if (!atCap) onChange([...selected, id]);
  };

  return (
    <SectionCard
      title="Firewall Settings"
      description="Your instances are launched with default security group settings that block all inbound and outbound traffic. Attach one or more security groups to permit access."
    >
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" style={sgTrigger}>
            {selectedSgs.length === 0 ? (
              <span style={{ color: 'var(--vn-fg-muted)' }}>Select security groups…</span>
            ) : (
              <span style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)', minWidth: 0 }}>
                {selectedSgs.map((sg) => (
                  <Badge key={sg.id} size="sm" appearance="subtle" tone="neutral">
                    {sg.name || sg.id}
                  </Badge>
                ))}
              </span>
            )}
            <ChevronDown size={16} style={{ marginLeft: 'auto', flexShrink: 0, color: 'var(--vn-fg-muted)' }} />
          </button>
        </PopoverTrigger>
        <PopoverContent style={{ width: 'var(--radix-popover-trigger-width)', padding: 0 }}>
          <div style={{ padding: 'var(--vn-space-2)', borderBottom: '1px solid var(--vn-border-default)' }}>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search security groups"
              startSlot={<Search size={15} />}
            />
          </div>
          <div style={{ maxHeight: 280, overflowY: 'auto', padding: 'var(--vn-space-2)', display: 'grid', gap: 'var(--vn-space-1)' }}>
            {filtered.length === 0 ? (
              <p style={{ margin: 0, padding: 'var(--vn-space-2)', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                No security groups match “{query}”.
              </p>
            ) : (
              filtered.map((sg) => {
                const checked = selected.includes(sg.id);
                const disabled = !checked && atCap;
                return (
                  <label
                    key={sg.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--vn-space-2)',
                      padding: 'var(--vn-space-2)',
                      borderRadius: 'var(--vn-radius-sm)',
                      fontSize: 'var(--vn-font-size-md)',
                      background: checked ? 'var(--vn-bg-subtle)' : 'transparent',
                      opacity: disabled ? 0.5 : 1,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <Checkbox checked={checked} disabled={disabled} onCheckedChange={() => toggle(sg.id)} />
                    <span>{sg.name || sg.id}</span>
                    <span style={{ marginLeft: 'auto', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                      {ruleCount(sg.template?.rule?.length ?? 0)}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>

      <p style={{ margin: 'var(--vn-space-2) 0 0', color: atCap ? 'var(--vn-warning-text)' : 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
        {selected.length}/{MAX_SECGROUPS} selected{atCap ? ' — limit reached' : ''}
      </p>

      {selectedSgs.length > 0 && (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)', marginTop: 'var(--vn-space-3)' }}>
          {selectedSgs.map((sg) => (
            <Disclosure
              key={sg.id}
              summary={`${sg.name || sg.id} · ${ruleCount(sg.template?.rule?.length ?? 0)}`}
            >
              <RuleLines rules={sg.template?.rule ?? []} />
            </Disclosure>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

/** Select-like trigger for the security-group multi-select. */
const sgTrigger: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--vn-space-2)',
  width: '100%',
  minHeight: 34, /* matches @ui md Select trigger height */
  padding: 'var(--vn-space-2) var(--vn-space-3)',
  border: '1px solid var(--vn-border-default)',
  borderRadius: 'var(--vn-radius-md)',
  background: 'var(--vn-bg-surface)',
  textAlign: 'left',
  cursor: 'pointer',
};
